import { Bidder } from '../../enums'
import {
  IS2SParams,
  IValidBidRequest,
  BidAdapter,
  IBidderRequest,
  IBidResponse,
  IExternalId,
  IS2SBidRequest
} from '../../../typings/IPrebid'
import { IRTBResponse } from '../../../typings/IOpenRTB'
import { registerBidder } from 'prebid.js/src/adapters/bidderFactory'
import { Cookie } from '../../helpers/cookie'
import Logger from '../../helpers/Logger'
import { Protocol } from '../../enums/Protocol'
import { getS2SBeta, getS2SUrl } from './BetaTest'
import { MediaType } from '../../enums/MediaType'
import { LiftTest } from '../../liftTest/LiftTest'
import { IKeywordResults } from '../../../typings/IKeywordResults'
import {
  cacheNativeTestBid,
  getNativeTestResponse
} from './helpers/native/nativeTestBids'
import { parseNativeResponse } from './helpers/native/parseNativeResponse'
import { getEIDs } from './helpers/eids'
import mvFetch from '../../helpers/mvFetch'
import {
  collectDeviceMetadata,
  recordNextrollInfo,
  IDeviceMetadata
} from './helpers/device'

import { addSlotLevelData } from './helpers/slot'

const priceEncryption = { partner: 'criteo' }

const log = (...messages) => Logger.custom('s2s', ...messages)

export class S2SAdapter implements BidAdapter {
  private readonly supportedMediaTypes = [
    MediaType.banner,
    MediaType.video,
    MediaType.native
  ]
  readonly code = Bidder.S2S

  static bidReqUrl: string

  /**
   * Interpolates the beta value onto bid request URL,
   * and stores at runtime
   */
  get bidRequestUrl(): string {
    if (S2SAdapter.bidReqUrl) {
      return S2SAdapter.bidReqUrl
    }

    const beta = getS2SBeta()
    const s2sUrl = getS2SUrl(window.$mediavine.web.model, beta)

    // register targeting
    window.googletag = window.googletag || {}
    window.googletag.cmd = window.googletag.cmd || []
    window.googletag.cmd.push(() => {
      const pubads = window.googletag.pubads()
      pubads.setTargeting('s2sVersion', beta.name)
    })

    const url = `${s2sUrl}/bidRequest/?s2sVersion=${beta.name}`
    S2SAdapter.bidReqUrl = url

    return url
  }

  static acVendors: Array<string> = []

  /**
   * grab AC string from additional consent cookie
   * trim version~ prefix, then make array of ids (strings)
   * and cache in class for rest of page view
   */
  get additionalConsent(): Array<string> | undefined {
    if (S2SAdapter.acVendors.length) {
      return S2SAdapter.acVendors
    }

    const acValue = localStorage.getItem('addtlConsent')
    if (!acValue) {
      return undefined
    }

    const vendors = acValue.split('~')[1]

    S2SAdapter.acVendors = vendors.split('.')
    return S2SAdapter.acVendors
  }

  get token(): IMVToken {
    const tokensCookie = new Cookie<IMVToken | null>({ name: 'mv_tokens' })
    try {
      return tokensCookie.value || { mv_uuid: undefined, version: undefined }
    } catch (e) {
      return { mv_uuid: undefined, version: undefined }
    }
  }

  buildRequests(
    validBidRequests: Array<IValidBidRequest<IS2SParams>>,
    bidderRequest: IBidderRequest
  ): { data: IBidRequestMetadata; url: string; method: 'POST' } {
    recordNextrollInfo()
    addSlotLevelData(bidderRequest.bids as Array<IS2SBidRequest>)
    const data: IBidRequestMetadata = {
      uuid: this.token.mv_uuid || '',
      uuidVersion: this.token.version,
      hash: window.$mediavine?.web?.hash,
      device: collectDeviceMetadata(),
      referrer: document.referrer,
      slug: window.$mediavine.web.model.slug,
      categories: window.$mediavine.web.model.categories,
      topLevelCategory: window.$mediavine.web.model.topLevelCategory,
      secure: document.location.protocol === Protocol.https ? '1' : '0',
      eids: getEIDs(this.token.mv_uuid),
      liftTestBidder: getLiftTestBidder(),
      ifa: window.$mediavine.idfa || undefined,
      additionalConsent: this.additionalConsent,
      keywords: getKeywordArrays(),
      requestModel: window.$mediavine.web.model.requestModel
    }

    cacheNativeTestBid(validBidRequests)
    const combinedData = {
      he: window.$mediavine.he ?? '',
      ...data,
      ...bidderRequest
    }
    log(`request data`, combinedData)

    return {
      data: combinedData,
      url: this.bidRequestUrl,
      method: 'POST'
    }
  }

  interpretResponse(
    { body }: { body: Array<object> },
    request
  ): Array<IBidResponse> {
    const bidResponses: Array<IBidResponse & IResponseMetadata> = []

    getNativeTestResponse(body, request)

    body.forEach((rtbResponse: IRTBResponse & IResponseMetadata) => {
      rtbResponse.seatbid.forEach((seatbid) => {
        seatbid.bid.forEach((bid) => {
          try {
            const bidResponse: IBidResponse & IResponseMetadata = {} as any
            if (!bid.adm) {
              return
            }
            bidResponse.s2sBidder = rtbResponse.bidder
            bidResponse.requestId = bid.impid
            bidResponse.ad = bid.adm
            bidResponse.creativeId = bid.crid
            bidResponse.campaignId = bid.cid
            bidResponse.cpm = bid.price
            bidResponse.unadjustedCpm = bid.unadjustedPrice || bid.price
            bidResponse.height = bid.h
            bidResponse.width = bid.w
            bidResponse.currency = 'USD'
            bidResponse.ttl = bid.exp
            bidResponse.netRevenue = true
            bidResponse.nurl = bid.nurl
            bidResponse.mediaType = rtbResponse.mediaType

            if (rtbResponse.mediaType === MediaType.video) {
              bid.adm.trim()[0] === '<'
                ? (bidResponse.vastXml = bid.adm)
                : (bidResponse.vastUrl = bid.adm)
            }

            if (rtbResponse.mediaType === MediaType.native) {
              bidResponse.mediaType = 'native'
              try {
                bidResponse.native = parseNativeResponse(bid)
              } catch (e) {
                log(e)
              }
            }

            bidResponses.push(bidResponse)
          } catch (e) {
            log('Error building response', e, rtbResponse)
          }
        })
      })
    })

    return bidResponses
  }

  getUserSyncs(syncOptions, serverResponses) {}
  isBidRequestValid() {
    return true
  }

  async onBidWon(bidResponse) {
    if (
      bidResponse.bidder === 'S2S' &&
      bidResponse.s2sBidder == priceEncryption.partner &&
      bidResponse.nurl
    ) {
      const response = mvFetch.fetch(bidResponse.nurl, 'POST')
    }
  }
  onSetTargeting() {}
  onTimeout() {}
}

const spec = new S2SAdapter()
registerBidder(spec)

function getLiftTestBidder():
  | { bidder: string; mediaType: { [type: string]: boolean } }
  | undefined {
  const liftTest = LiftTest.new(window.$mediavine.web.model)

  if (liftTest.bidder && liftTest.mediaType && liftTest.isS2S()) {
    const mediaType = {
      video: liftTest.isVideo(),
      banner: liftTest.isDisplay(),
      outstream: liftTest.isOutstream(),
      ibv: liftTest.isOutstream()
    }
    return { bidder: liftTest.bidder, mediaType }
  }
}

/**
 * keyword service feeds these in a CSV string
 * to interface with GAM, but partners normally
 * want arrays, so send it to s2s prepped
 */
function getKeywordArrays(): KeywordProperties {
  const { sales_kw, sales_cat, sales_safety } =
    window.$mediavine.web.keywords || {}

  return {
    sales_kw: sales_kw?.split(','),
    sales_cat: sales_cat?.split(','),
    sales_safety: sales_safety?.split(',')
  }
}

interface IResponseMetadata {
  unadjustedCpm: number
  netRevenue: boolean
  nurl?: string
  mediaType: string
}

type KeywordProperties = {
  [key in keyof IKeywordResults]: Array<string>
}

interface IBidRequestMetadata {
  uuid: string
  uuidVersion?: number
  hash?: string
  device: IDeviceMetadata
  referrer: string
  slug: string
  secure: '1' | '0'
  categories: Array<ICategory>
  topLevelCategory: ICategory
  eids: Array<IExternalId>
  liftTestBidder?: { bidder: string; mediaType: { [type: string]: boolean } }
  ifa: string | undefined
  additionalConsent?: Array<string>
  keywords?: KeywordProperties
  requestModel: { [key: string]: string }
}
